import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { completeLogin, startLogin } from '../lib/auth';
import { useAuth } from '../lib/AuthContext';

export default function AuthCallback() {
  const navigate = useNavigate();
  const auth = useAuth();
  const [err, setErr] = useState<string | null>(null);
  // The authorization code is single-use. This effect must run exactly once
  // per page load — a second run (StrictMode, or any re-render that changes
  // the deps) would re-POST the consumed code and Cognito returns
  // invalid_grant. A ref guard survives both. We also strip the query string
  // so a manual refresh of this URL can't replay a spent code.
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;

    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    const state = params.get('state');
    const error = params.get('error');
    window.history.replaceState({}, '', '/auth/callback');

    if (error) {
      setErr(`${error}: ${params.get('error_description') ?? ''}`);
      return;
    }
    if (!code) {
      // No code to exchange (e.g. a refresh after we stripped it) — restart
      // the auth-code flow rather than dead-ending on an error.
      void startLogin('/');
      return;
    }

    completeLogin(code, state)
      .then((returnTo) => {
        auth.refresh();
        navigate(returnTo || '/', { replace: true });
      })
      .catch((e) => setErr((e as Error).message));
    // Runs once; auth/navigate are intentionally not deps (guarded by `ran`).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen grid place-items-center">
      {err ? (
        <div className="max-w-md rounded border border-red-500/30 bg-red-950/40 p-6">
          <div className="mb-2 text-sm text-red-300">sign-in failed</div>
          <div className="text-sm text-white/80">{err}</div>
          <a
            href="/"
            className="mt-4 inline-block text-sm text-[var(--color-brand-green)] hover:underline"
          >
            ← back
          </a>
        </div>
      ) : (
        <div className="text-sm text-[var(--text-secondary)]">Completing sign-in…</div>
      )}
    </div>
  );
}
